import type { SingleValue } from '@ksyos/design-system';
import type { ChangeEvent } from 'react';
import { Box, FormGroup, Select, TextInput } from '@ksyos/design-system';
import type { ValidationErrors } from '@frontend/utils';
import { FinanceSearchField } from '@common/enums/financeSearchField';
import { getSelectedLanguage } from '@frontend/translate';
import { getValidationErrorTranslation } from '@frontend/utils/getValidationErrorTranslation';

type SearchForFilterProps = {
    errors: ValidationErrors;
    onChangeSearchField: (value: FinanceSearchField | undefined) => void;
    onChangeSearchText: (value: string | undefined) => void;
    searchField?: FinanceSearchField;
    searchText?: string;
};

export function SearchForFilter({
    searchField,
    searchText,
    errors,
    onChangeSearchText,
    onChangeSearchField,
}: SearchForFilterProps) {
    const { processSearchField, common, finance } = getSelectedLanguage();

    function handleSearchFieldChange(
        item:
            | SingleValue<{
                  label: string;
                  value: FinanceSearchField;
              }>
            | undefined
    ) {
        onChangeSearchField(item?.value);
    }

    function handleSearchTextChange(event: ChangeEvent<HTMLInputElement>) {
        const textValue = event.target.value || undefined;

        onChangeSearchText(textValue);
    }

    return (
        <FormGroup
            hasErrors={errors.has('searchText') || errors.has('searchField')}
            label={common.searchFor}
            tooltipText={finance.searchForTooltip}
        >
            <Box sx={{ display: 'flex' }}>
                <Box width={'55%'}>
                    <TextInput
                        error={getValidationErrorTranslation(errors.get('searchText'))}
                        onChange={handleSearchTextChange}
                        sx={{ marginRight: 2, width: 'unset' }}
                        value={searchText || ''}
                    />
                </Box>
                <Box width={'45%'}>
                    <Select
                        error={getValidationErrorTranslation(errors.get('searchField'))}
                        isClearable={true}
                        onChange={handleSearchFieldChange}
                        options={getSearchFieldOptions()}
                        placeholder={common.searchIn}
                        sx={{ width: 'unset' }}
                        value={
                            searchField && {
                                label: processSearchField[searchField],
                                value: searchField,
                            }
                        }
                    />
                </Box>
            </Box>
        </FormGroup>
    );

    function getSearchFieldOptions() {
        return Object.values(FinanceSearchField).map((field) => {
            return {
                label: processSearchField[field],
                value: field,
            };
        });
    }
}
